import { Table, Divider, Flex } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useInvestigationsServiceGetListSpecimensEndpoint } from 'src/api/queries'
import { Permissions, SpecimenItemDto, SpecimenStatus } from 'src/api/requests'
import { GlassIcon } from 'src/assets'
import {
  PageBreadcrumbs,
  Paragraph,
  RangePicker,
  SearchBar,
  Select,
  TableActionButtons,
  TbTag
} from 'src/components'
import { AssignInvestigationModal } from 'src/features/persons/components'
import { TestStatusTag } from 'src/features/tests/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

export const InstitutionInvestigationPage = () => {
  const { t, currentLang } = useLocales()

  const [investigationId, setInvestigationId] = useState<string>()
  const [filters, setFilters] = useState<Record<string, string | undefined>>()
  const [dateTo, setDateTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [status, setStatus] = useState<string>()

  const pagination = useTablePagination()
  const { userData, hasPermission } = useUser()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    demandInstitutionIds: [String(userData?.currentLegalEntityId)],
    ...filters,
    ...(dateTo && {
      createdAtTo: dateTo
    }),
    ...(dateFrom && {
      createdAtFrom: dateFrom
    }),
    ...(status && {
      status: [status as SpecimenStatus]
    })
  }

  const { data, isLoading } =
    useInvestigationsServiceGetListSpecimensEndpoint(queryParams)

  const onSearch = (val: string) => {
    setFilters((prev) => ({ ...prev, searchTerm: val ? val : undefined }))
    onFiltersChange()
  }

  const onFiltersChange = () => {
    resetPagination(pagination)
  }

  const onStatusChange = (val: string) => {
    setStatus(val)
    onFiltersChange()
  }

  const onCloseModal = () => setInvestigationId(undefined)

  const getActionItems = (id: string, status: string) => [
    ...(hasPermission(Permissions.INVESTIGATIONS_SPECIMENS_HARVEST) &&
    status === SpecimenStatus.CREATED
      ? [
          {
            key: 'assign',
            label: t('investigations.assign_investigation_demand'),
            onClick: () => setInvestigationId(id),
            icon: <GlassIcon />
          }
        ]
      : [])
  ]

  const columns: ColumnsType<SpecimenItemDto> = [
    {
      key: 'investigation number',
      title: t('investigations.investigation_number'),
      render: (_, { code, isTb }) => (
        <Flex gap={8} align="center">
          {code}
          <TbTag isTb={isTb} />
        </Flex>
      )
    },
    {
      key: 'created date',
      title: t('investigations.created_date'),
      render: ({ createdAt }) =>
        dayjs.utc(createdAt).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'specimen type',
      title: t('consultations.specimen_type'),
      render: (_, { type, status }) => {
        if (!type) return '-'

        return status === SpecimenStatus.CREATED
          ? '-'
          : (type?.translation?.[currentLang.value] ?? '-')
      }
    },
    {
      key: 'last name',
      dataIndex: ['person', 'lastName'],
      title: t('form.last_name')
    },
    {
      key: 'first name',
      dataIndex: ['person', 'firstName'],
      title: t('form.first_name')
    },

    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => <TestStatusTag status={status} />
    },
    {
      key: 'test type',
      title: t('tests_results.type'),
      render: (_, { demand }) => {
        const text = (
          <div>
            {demand?.categories?.map((item) => (
              <div key={item.id}>{item.translation?.[currentLang.value]}</div>
            ))}
          </div>
        )

        return (
          <Paragraph
            ellipsis={{
              rows: 2,
              tooltip: text
            }}
          >
            {text}
          </Paragraph>
        )
      }
    },

    {
      key: 'actions',
      fixed: 'right',
      render: (record) =>
        !record?.harvestAt && (
          <TableActionButtons
            justify="center"
            items={getActionItems(record.id, record.status)}
          />
        )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.tests')}
      items={[{ title: t('menu.tests') }]}
    >
      <Flex justify="space-between">
        <SearchBar
          maxWidth={300}
          onSearch={onSearch}
          placeholder={t('investigations.search_placeholder')}
        />

        <Flex gap={8}>
          <RangePicker
            setDateTo={setDateTo}
            setDateFrom={setDateFrom}
            resetPagination={onFiltersChange}
          />

          <Select
            options={Object.values(SpecimenStatus).map((status) => ({
              label: t(`tests.statuses.${status}`),
              value: status
            }))}
            onChange={onStatusChange}
            onClear={() => onStatusChange('')}
            allowClear
            placeholder={t('form.status')}
          />
        </Flex>
      </Flex>

      {investigationId && (
        <AssignInvestigationModal
          id={investigationId}
          onCancel={onCloseModal}
        />
      )}

      <Divider />

      <Table
        bordered
        dataSource={data?.data}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
